import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

export default function PlanRenove() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Plan Renove | Innotec Food Equipment</title>
        <meta name="description" content="Renueva tu maquinaria industrial alimentaria con el Plan Renove de Innotec Food Equipment. Mejora tu productividad con equipos de última generación." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[75vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] to-transparent z-10"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed z-0"></div>
        </div>

        <div className="container mx-auto px-6 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-3 py-1 text-sm bg-[#FCAA0D] text-[#0A0A0B] rounded-full mb-6">
              Innovación Continua
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Plan <span className="text-[#FCAA0D]">Renove</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Actualiza tu maquinaria industrial con la última tecnología y mejora la eficiencia
              de tu planta productiva con nuestro programa Plan Renove de Innotec Food.
            </p>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-xl mb-8">
              <p className="text-2xl font-medium text-white italic">
                "Renovarse es <span className="text-[#FCAA0D]">avanzar hacia el futuro</span>"
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Descripción */}
      <section className="py-20 bg-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/30 transition-all duration-500"
            >
              <p className="text-lg text-white/80 leading-relaxed">
                Nuestro Plan Renove está diseñado para que puedas actualizar tu maquinaria industrial
                alimentaria con equipos de última generación, mejorando tu productividad y reduciendo costes
                operativos a largo plazo.
              </p>
              <p className="text-lg text-white/80 leading-relaxed mt-4">
                Entregando tu maquinaria usada, recibirás un descuento considerable en la adquisición
                de nuevos equipos, permitiéndote acceder a tecnología más avanzada y eficiente.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex items-center justify-center"
            >
              <img
                src="https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=1170"
                alt="Maquinaria de última generación"
                className="rounded-xl shadow-lg max-h-[300px] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              BENEFICIOS DEL <span className="text-[#FCAA0D]">PLAN RENOVE</span>
            </h3>

            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium text-white mb-3">Ahorro Económico</h4>
                <p className="text-white/80">
                  Descuentos significativos en la adquisición de maquinaria nueva al entregar tu equipo usado
                  como parte de pago.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium text-white mb-3">Mayor Eficiencia</h4>
                <p className="text-white/80">
                  Accede a tecnología de última generación que optimiza tus procesos productivos,
                  reduce consumos energéticos y mejora la calidad.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-[#FCAA0D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium text-white mb-3">Garantía Extendida</h4>
                <p className="text-white/80">
                  Los equipos adquiridos a través del Plan Renove cuentan con garantías extendidas
                  y soporte técnico preferencial.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]/90 relative">
        <div className="absolute inset-0 opacity-10">
          <div className="grid-background"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-12 text-center">
              ¿CÓMO <span className="text-[#FCAA0D]">FUNCIONA</span>?
            </h3>

            <div className="space-y-16">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="md:w-1/3 flex justify-center"
                >
                  <div className="h-24 w-24 rounded-full bg-[#FCAA0D] flex items-center justify-center text-[#0A0A0B] font-bold text-3xl">
                    1
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="md:w-2/3"
                >
                  <h4 className="text-xl font-medium text-white mb-3">Solicitud de valoración</h4>
                  <p className="text-white/80 text-lg">
                    Rellena el formulario con los datos de tu maquinaria actual. Nuestros especialistas
                    evaluarán su estado y te proporcionarán una valoración personalizada para su aceptación
                    como parte de pago.
                  </p>
                </motion.div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="md:w-1/3 flex justify-center order-1 md:order-2"
                >
                  <div className="h-24 w-24 rounded-full bg-[#FCAA0D] flex items-center justify-center text-[#0A0A0B] font-bold text-3xl">
                    2
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="md:w-2/3 order-2 md:order-1"
                >
                  <h4 className="text-xl font-medium text-white mb-3">Propuesta personalizada</h4>
                  <p className="text-white/80 text-lg">
                    Te presentaremos las opciones de nueva maquinaria que mejor se adapten a tus necesidades,
                    con el descuento aplicado por la entrega de tu equipo usado. Incluiremos información detallada
                    sobre especificaciones técnicas, ventajas y rendimiento esperado.
                  </p>
                </motion.div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="md:w-1/3 flex justify-center"
                >
                  <div className="h-24 w-24 rounded-full bg-[#FCAA0D] flex items-center justify-center text-[#0A0A0B] font-bold text-3xl">
                    3
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="md:w-2/3"
                >
                  <h4 className="text-xl font-medium text-white mb-3">Implementación y formación</h4>
                  <p className="text-white/80 text-lg">
                    Nos encargamos de la instalación de los nuevos equipos y la retirada de los antiguos.
                    Además, ofrecemos formación a tu personal para asegurar una transición rápida y efectiva
                    a la nueva tecnología.
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <div className="max-w-4xl mx-auto p-6 bg-white/5 border border-white/20 rounded-xl">
            <p className="text-white/80 text-lg">
              El Plan Renove de Innotec Food está diseñado para facilitar la modernización de tu planta productiva,
              permitiéndote optimizar tu inversión y mejorar tu competitividad en el mercado.
              Nuestro equipo técnico te asesorará en todo el proceso para garantizar que obtengas
              el máximo beneficio de este programa.
            </p>
          </div>
        </div>
      </section>

      {/* Modalidades Plan Renove */}
      <section className="py-20 bg-[url('https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=1170')] bg-cover bg-center bg-fixed relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/90 via-[#0A0A0B]/80 to-[#0A0A0B]/90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto bg-[#FCAA0D]/10 backdrop-blur-sm border border-[#FCAA0D]/30 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.3)] transition-all mb-16"
          >
            <h3 className="text-3xl font-bold text-[#FCAA0D] mb-6 text-center">
              PLAN RENOVE <span className="text-white">PREMIUM</span>
            </h3>
            <p className="text-white/80 text-lg mb-6">
              Para empresas que buscan una renovación completa de su línea de producción ofrecemos el Plan Renove Premium.
            </p>
            <p className="text-white/80 mb-6">
              Además de los beneficios estándar del Plan Renove, esta modalidad exclusiva incluye:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                "Descuentos superiores en la adquisición de equipos",
                "Financiación preferencial con condiciones exclusivas",
                "Servicio técnico 24/7 durante el primer año",
                "Formación avanzada para tu personal técnico",
                "Mantenimiento preventivo trimestral incluido",
                "Estudio de optimización de la línea de producción"
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <span className="text-[#FCAA0D] mr-2">✓</span>
                  <span className="text-white/80">{item}</span>
                </motion.li>
              ))}
            </ul>
            <p className="text-xl text-white text-center font-medium">
              Lleva tu planta de producción al <span className="text-[#FCAA0D]">siguiente nivel</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              Plazas <span className="text-[#FCAA0D]">limitadas</span>
            </h3>
            <p className="text-lg text-white/80 mb-8">
              El Plan Renove de Innotec Food está disponible para un número limitado de empresas cada año.
              La disponibilidad de este programa está sujeta a la capacidad técnica para la correcta implementación
              y seguimiento de los proyectos.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Si estás interesado en renovar tu maquinaria industrial alimentaria con condiciones ventajosas,
              contacta con nosotros lo antes posible para asegurar tu plaza en el programa.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/catalogo-maquinaria"
              className="bg-white px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] transition-all"
            >
              <span>Ver Catálogo</span>
              <span className="text-sm bg-[#FCAA0D] px-2 py-1 rounded">Maquinaria nueva</span>
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/solicitar-valoracion"
              className="bg-[#FCAA0D] px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(252,170,13,0.4)] transition-all"
            >
              <span>Solicitar Valoración</span>
              <span className="text-sm bg-white px-2 py-1 rounded">Maquinaria usada</span>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574027107792-c96351c3c8ac?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Moderniza tu fábrica con el <span className="text-[#FCAA0D]">Plan Renove</span>
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-3xl mx-auto">
              Actualiza tu tecnología, reduce costes operativos e incrementa tu capacidad productiva.
              Invierte en el futuro de tu empresa con equipos más eficientes y sostenibles.
            </p>
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-12 max-w-2xl mx-auto shadow-[0_0_20px_rgba(252,170,13,0.1)]">
              <p className="text-2xl font-semibold text-white">
                ¿Listo para dar el salto tecnológico? Contacta con nosotros <span className="text-[#FCAA0D]">AHORA</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center">
              <a
                href="/contacto"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-[#0A0A0B] font-medium py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(252,170,13,0.3)]"
              >
                Solicitar información
              </a>
              <a
                href="/formulario-plan-renove"
                className="bg-white/5 border border-white/20 text-white hover:border-[#FCAA0D] hover:shadow-[0_0_15px_rgba(252,170,13,0.2)] font-medium py-4 px-8 rounded-lg transition-all"
              >
                Acceder al formulario
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
